import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GpsFixedRounded as FocusIcon,
  ScheduleOutlined as TimelineIcon,
  ScheduleRounded as TimeIcon,
  VisibilityOffOutlined as HiddenIcon,
  VisibilityOutlined as VisibleIcon,
} from '@mui/icons-material';
import type { ExecutionPhase, ExecutionTripViewModel } from './executionTypes';

interface ExecutionTripCardProps {
  trip: ExecutionTripViewModel;
  active: boolean;
  highlighted?: boolean;
  visible: boolean;
  onOpen: () => void;
  onToggleVisibility: () => void;
  onFocus: () => void;
  onOpenTimeline: () => void;
}

const PHASE_TONE: Record<ExecutionPhase, 'default' | 'info' | 'warning' | 'success'> = {
  PENDING: 'default',
  ACTIVE: 'info',
  DELAYED: 'warning',
  COMPLETED: 'success',
};

const Metric: React.FC<{ label: string; value: string; warning?: boolean }> = ({ label, value, warning }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 9.5, color: 'text.secondary', lineHeight: 1.15 }}>{label}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 750, color: warning ? 'warning.dark' : 'text.primary', lineHeight: 1.25 }} noWrap>{value}</Typography>
  </Box>
);

const ExecutionTripCard: React.FC<ExecutionTripCardProps> = ({
  trip,
  active,
  highlighted = false,
  visible,
  onOpen,
  onToggleVisibility,
  onFocus,
  onOpenTimeline,
}) => {
  const theme = useTheme();
  const { plannedTrip, metrics } = trip;

  return (
    <Paper
      variant="outlined"
      data-testid={`execution-trip-card-${trip.id}`}
      onClick={onOpen}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        mb: 1,
        px: 1.35,
        py: 1.15,
        pl: 1.65,
        borderRadius: 2.5,
        cursor: 'pointer',
        borderColor: active || highlighted ? alpha(trip.color, 0.58) : alpha(theme.palette.divider, 0.9),
        boxShadow: active || highlighted ? `0 14px 28px ${alpha(trip.color, 0.15)}` : '0 3px 12px rgba(15,23,42,0.04)',
        opacity: visible ? 1 : 0.68,
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.09)}` },
        '&::before': { content: '""', position: 'absolute', inset: '0 auto 0 0', width: 4, bgcolor: trip.color },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.55} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={800}>{trip.id}</Typography>
            <Chip size="small" color={PHASE_TONE[trip.phase]} label={trip.phase.replace('_', ' ')} sx={{ height: 20, fontSize: 9.5, fontWeight: 750 }} />
            {trip.attentionReasons.length ? <Chip size="small" color="warning" variant="outlined" label={`${trip.attentionReasons.length} attention`} sx={{ height: 20, fontSize: 9.5 }} /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {plannedTrip.vehicle.number || plannedTrip.vehicle.id || 'Unassigned vehicle'} · {plannedTrip.driver.name || 'Unassigned driver'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Open timeline">
            <IconButton
              size="small"
              aria-label={`Open Timeline for ${trip.id}`}
              onClick={(event) => { event.stopPropagation(); onOpenTimeline(); }}
              sx={{
                width: 28,
                height: 28,
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.12),
                bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.72),
              }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={visible ? 'Hide Trip on map' : 'Show Trip on map'}>
            <IconButton
              size="small"
              aria-label={visible ? `Hide ${trip.id}` : `Show ${trip.id}`}
              onClick={(event) => { event.stopPropagation(); onToggleVisibility(); }}
            >
              {visible ? <VisibleIcon sx={{ fontSize: 18 }} /> : <HiddenIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Focus Trip on map">
            <IconButton
              size="small"
              aria-label={`Focus ${trip.id}`}
              onClick={(event) => { event.stopPropagation(); onFocus(); }}
            >
              <FocusIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ mt: 0.85, display: 'grid', gridTemplateColumns: '1.35fr 1fr 1fr 1fr', gap: 0.8 }}>
        <Metric label="CURRENT / NEXT" value={`${trip.currentPointLabel} → ${trip.nextPointLabel}`} />
        <Metric label="KM P / A" value={`${metrics.plannedComparableDistanceKm.toFixed(1)} / ${metrics.actualDistanceKm.toFixed(1)}`} />
        <Metric label="HRS P / A" value={`${(metrics.plannedComparableDurationMinutes / 60).toFixed(1)} / ${(metrics.actualDurationMinutes / 60).toFixed(1)}`} />
        <Metric label="SUCCESS / RTO" value={`${metrics.successCount} / ${metrics.rtoCount}`} warning={metrics.rtoCount > 0} />
      </Box>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.9 }}>
        <LinearProgress
          variant="determinate"
          value={metrics.progressPercent}
          sx={{ flex: 1, height: 5, borderRadius: 999, bgcolor: alpha(trip.color, 0.12), '& .MuiLinearProgress-bar': { bgcolor: trip.color } }}
        />
        <Typography variant="caption" fontWeight={750}>{Math.round(metrics.progressPercent)}%</Typography>
        {metrics.delayMinutes > 0 ? (
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: metrics.delayMinutes >= 20 ? 'warning.dark' : 'text.secondary' }}>
            <TimeIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={700}>+{metrics.delayMinutes}m</Typography>
          </Stack>
        ) : null}
      </Stack>
      {trip.attentionReasons.length ? (
        <Typography variant="caption" color="warning.dark" display="block" noWrap sx={{ mt: 0.55 }}>
          {trip.attentionReasons.join(' · ')}
        </Typography>
      ) : null}
    </Paper>
  );
};

export default ExecutionTripCard;
